// Theme + design tokens for the admin search UI. Mirrors the centralized
// styling guide (docs/STYLING_GUIDE.md in the platform team's repo): MUI v7
// theme + Emotion CSS-in-JS, single source of truth for colors, spacing,
// transitions, and DataGrid presets.

import styled from "@emotion/styled";
import { createTheme } from "@mui/material/styles";

// ------------------------------------------------------------
// Design tokens
// ------------------------------------------------------------

export const colors = {
  primary: "#009AFF",
  secondary: "#62AD42",
  error: "#f44336",
  warning: "#FF9800",
  info: "#2196F3",
  success: "#4CAF50",

  gray50: "#f8fafc",
  gray100: "#f1f5f9",
  gray200: "#e2e8f0",
  gray300: "#cbd5e1",
  gray400: "#94a3b8",
  gray500: "#64748b",
  gray600: "#475569",
  gray700: "#334155",
  gray800: "#1e293b",
  gray900: "#0f172a",
} as const;

export const spacing = {
  xs: "4px",
  sm: "8px",
  md: "16px",
  lg: "24px",
  xl: "32px",
  xxl: "48px",
} as const;

export const transitions = {
  fast: "0.15s ease",
  normal: "0.2s ease",
} as const;

// ------------------------------------------------------------
// MUI theme
// ------------------------------------------------------------

export const muiTheme = createTheme({
  palette: {
    primary: { main: colors.primary },
    secondary: { main: colors.secondary },
    error: { main: colors.error },
    warning: { main: colors.warning },
    info: { main: colors.info },
    success: { main: colors.success },
    background: {
      default: colors.gray50,
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 14,
  },
  shape: {
    borderRadius: 8,
  },
});

// ------------------------------------------------------------
// DataGrid styling preset (compact)
// ------------------------------------------------------------

export const dataGridStyles = {
  compact: {
    backgroundColor: "white",
    borderRadius: "12px",
    border: `1px solid ${colors.gray200}`,
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.gray50,
      borderBottom: `2px solid ${colors.gray200}`,
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 600,
      fontSize: "0.75rem",
      color: colors.gray500,
      textTransform: "uppercase" as const,
      letterSpacing: "0.03em",
    },
    "& .MuiDataGrid-cell": {
      borderBottom: `1px solid ${colors.gray100}`,
      fontSize: "0.85rem",
    },
    "& .MuiDataGrid-row:hover": {
      backgroundColor: colors.gray50,
    },
  },
};

// ------------------------------------------------------------
// Layout primitives
// ------------------------------------------------------------

export const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${spacing.lg};
`;

export const PageHeader = styled.header`
  margin-bottom: ${spacing.lg};
`;

export const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${colors.gray900};
  margin: 0 0 ${spacing.xs} 0;
`;

export const PageSubtitle = styled.p`
  font-size: 0.875rem;
  color: ${colors.gray500};
  margin: 0;
`;

export const SectionLabel = styled.span`
  font-size: 0.75rem;
  color: ${colors.gray500};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 600;
`;
